// =============================================================================
// PHOTOS CHANTIER - Composant de gestion des photos terrain
// =============================================================================
// Fonctionnalités :
// - Upload de nouvelles photos
// - Import/Scan depuis les photos existantes du dossier Commercial
// - Visualisation avec catégories (Avant/Pendant/Après)
// - Suppression et téléchargement
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Camera, Upload, Download, Trash2, Eye, X, Check, FolderOpen,
  Image, ArrowRight, FileImage, CheckCircle2, AlertCircle, RefreshCw, Shield
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { toast } from "sonner";
import { useConfigList } from "../../hooks/useConfigList";
import { formatDateFR } from "../../utils/dateFormat";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhotosChantier({ 
  dossierId, 
  tenantId, 
  verrouille = false,
  clientNom = "",
  dossierRef = "",
  ficheCEE = null
}) {
  const { items: photoCatItems } = useConfigList("photo_categories");
  const PHOTO_CATEGORIES = photoCatItems.length > 0
    ? photoCatItems.map(c => ({ value: c.id, label: c.label, color: c.color || "bg-[var(--oh-accent-solid)]" }))
    : [
        { value: "avant", label: "Avant travaux", color: "bg-[var(--oh-accent-solid)]" },
        { value: "pendant", label: "Pendant travaux", color: "bg-orange-500" },
        { value: "apres", label: "Après travaux", color: "bg-emerald-500" },
        { value: "detail", label: "Détail technique", color: "bg-[var(--oh-accent-solid)]" },
      ];
  // État photos chantier (stockées dans workflow_data.operation.photos)
  const [photosChantier, setPhotosChantier] = useState([]);
  // État photos existantes du dossier (pour import)
  const [photosExistantes, setPhotosExistantes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  
  // Dialog import
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedForImport, setSelectedForImport] = useState(new Set());
  
  // Dialog Certificall
  const [showCertificallDialog, setShowCertificallDialog] = useState(false);
  const [certificallCases, setCertificallCases] = useState([]);
  const [certificallLoading, setCertificallLoading] = useState(false);
  const [certificallConfigured, setCertificallConfigured] = useState(false);
  
  // Dialog preview
  const [previewPhoto, setPreviewPhoto] = useState(null);
  
  // Catégorie sélectionnée pour upload
  const [uploadCategory, setUploadCategory] = useState("pendant");
  
  // URLs des images (pour affichage)
  const [imageUrls, setImageUrls] = useState({});
  
  const headers = { "X-Tenant-ID": tenantId };

  // Charger les photos chantier depuis workflow_data
  const loadPhotosChantier = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, { 
        headers, 
        credentials: 'include' 
      });
      if (res.ok) {
        const dossier = await res.json();
        const photos = dossier.workflow_data?.operation?.photos_chantier || [];
        setPhotosChantier(photos);
      }
    } catch (err) {
      console.error("Erreur chargement photos chantier:", err);
    }
  }, [dossierId, tenantId]);

  // Charger les photos existantes du dossier (onglet Photos)
  const loadPhotosExistantes = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}/photos`, { 
        headers, 
        credentials: 'include' 
      });
      if (res.ok) {
        const data = await res.json();
        setPhotosExistantes(data || []);
      }
    } catch (err) {
      console.error("Erreur chargement photos existantes:", err);
    }
  }, [dossierId, tenantId]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadPhotosChantier(), loadPhotosExistantes(), checkCertificallConfig()])
      .finally(() => setLoading(false));
  }, [loadPhotosChantier, loadPhotosExistantes]);

  // Vérifier si Certificall est configuré
  const checkCertificallConfig = async () => {
    try {
      const res = await fetch(`${API}/api/certificall/health`);
      if (res.ok) {
        const data = await res.json();
        setCertificallConfigured(data.success === true);
      }
    } catch (err) {
      setCertificallConfigured(false);
    }
  };

  // Charger les dossiers Certificall
  const loadCertificallCases = async () => {
    setCertificallLoading(true);
    try {
      // Chercher par référence du dossier OmniHub
      const res = await fetch(
        `${API}/api/certificall/cases?reference=${encodeURIComponent(dossierRef)}&hours=720`,
        { headers, credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setCertificallCases(data.cases || []);
      }
    } catch (err) {
      console.error("Erreur chargement Certificall:", err);
      toast.error("Erreur de connexion à Certificall");
    } finally {
      setCertificallLoading(false);
    }
  };

  // Importer depuis Certificall
  const handleCertificallImport = async (caseId) => {
    try {
      const res = await fetch(`${API}/api/certificall/import/${dossierId}`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          case_id: caseId,
          category: uploadCategory
        }),
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.imported} photo(s) importée(s) depuis Certificall`);
        setShowCertificallDialog(false);
        loadPhotosChantier();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'import");
      }
    } catch (err) {
      console.error("Erreur import Certificall:", err);
      toast.error("Erreur lors de l'import");
    }
  };

  // Charger les URLs des images pour affichage
  useEffect(() => {
    const loadUrls = async () => {
      const urls = {};
      
      // Photos chantier
      for (const photo of photosChantier) {
        if (photo.source_id) {
          try {
            const res = await fetch(
              `${API}/api/metier/dossiers/${dossierId}/photos/${photo.source_id}/file`,
              { headers, credentials: 'include' }
            );
            if (res.ok) {
              const blob = await res.blob();
              urls[photo.id || photo.source_id] = URL.createObjectURL(blob);
            }
          } catch (err) {
            console.error("Erreur chargement image:", err);
          }
        }
      }
      
      // Photos existantes (pour preview dans import dialog)
      for (const photo of photosExistantes) {
        if (!urls[photo.id]) {
          try {
            const res = await fetch(
              `${API}/api/metier/dossiers/${dossierId}/photos/${photo.id}/file`,
              { headers, credentials: 'include' }
            );
            if (res.ok) {
              const blob = await res.blob();
              urls[photo.id] = URL.createObjectURL(blob);
            }
          } catch (err) {
            console.error("Erreur chargement image:", err);
          }
        }
      }
      
      setImageUrls(urls);
    };
    
    if (photosChantier.length > 0 || photosExistantes.length > 0) {
      loadUrls();
    }
  }, [photosChantier, photosExistantes, dossierId, tenantId]);

  // Upload de nouvelles photos
  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    const newPhotos = [...photosChantier];
    
    for (const file of files) {
      try {
        // D'abord, upload dans la collection photos du dossier
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", "photo_chantier");
        formData.append("description", `Photo chantier - ${uploadCategory}`);
        
        const uploadRes = await fetch(`${API}/api/metier/dossiers/${dossierId}/photos`, {
          method: "POST",
          headers: { "X-Tenant-ID": tenantId },
          body: formData,
          credentials: 'include'
        });
        
        if (uploadRes.ok) {
          const uploadedPhoto = await uploadRes.json();
          
          // Ajouter à la liste photos chantier
          newPhotos.push({
            id: `chantier_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            source_id: uploadedPhoto.id,
            filename: file.name,
            category: uploadCategory,
            date_ajout: new Date().toISOString(),
            origine: "upload"
          });
        }
      } catch (err) {
        console.error("Erreur upload:", err);
        toast.error(`Erreur lors de l'upload de ${file.name}`);
      }
    }
    
    // Sauvegarder dans workflow_data
    await savePhotosChantier(newPhotos);
    setUploading(false);
    toast.success(`${files.length} photo(s) ajoutée(s)`);
    
    // Reset input
    e.target.value = "";
  };

  // Importer des photos existantes
  const handleImport = async () => {
    if (selectedForImport.size === 0) {
      toast.error("Sélectionnez au moins une photo");
      return;
    }
    
    const newPhotos = [...photosChantier];
    
    for (const photoId of selectedForImport) {
      const existingPhoto = photosExistantes.find(p => p.id === photoId);
      if (existingPhoto) {
        // Vérifier qu'elle n'est pas déjà importée
        if (!newPhotos.some(p => p.source_id === photoId)) {
          newPhotos.push({
            id: `chantier_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            source_id: photoId,
            filename: existingPhoto.filename || existingPhoto.nom,
            category: uploadCategory,
            date_ajout: new Date().toISOString(),
            origine: "import_dossier"
          });
        }
      }
    }
    
    await savePhotosChantier(newPhotos);
    setShowImportDialog(false);
    setSelectedForImport(new Set());
    toast.success(`${selectedForImport.size} photo(s) importée(s)`);
  };

  // Supprimer une photo chantier (de la liste, pas du dossier)
  const handleDelete = async (photoId) => {
    if (!confirm("Retirer cette photo du chantier ?")) return;
    
    const newPhotos = photosChantier.filter(p => p.id !== photoId);
    await savePhotosChantier(newPhotos);
    toast.success("Photo retirée");
  };

  // Sauvegarder les photos chantier dans workflow_data.operation.photos_chantier
  const savePhotosChantier = async (photos) => {
    try {
      const res = await fetch(`${API}/api/metier/dossiers/${dossierId}`, {
        method: "PATCH",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          workflow_data: {
            operation: {
              photos_chantier: photos
            }
          }
        }),
        credentials: 'include'
      });
      
      if (res.ok) {
        setPhotosChantier(photos);
      } else {
        throw new Error("Erreur sauvegarde");
      }
    } catch (err) {
      console.error("Erreur sauvegarde photos chantier:", err);
      toast.error("Erreur lors de la sauvegarde");
    }
  };

  // Télécharger une photo
  const handleDownload = async (photo) => {
    try {
      const res = await fetch(
        `${API}/api/metier/dossiers/${dossierId}/photos/${photo.source_id}/file`,
        { headers, credentials: 'include' }
      );
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = photo.filename || 'photo_chantier.jpg';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      toast.error("Erreur téléchargement");
    }
  };

  // Toggle sélection pour import
  const toggleImportSelection = (photoId) => {
    const newSelection = new Set(selectedForImport);
    if (newSelection.has(photoId)) {
      newSelection.delete(photoId);
    } else {
      newSelection.add(photoId);
    }
    setSelectedForImport(newSelection);
  };

  // Grouper photos par catégorie
  const photosByCategory = PHOTO_CATEGORIES.reduce((acc, cat) => {
    acc[cat.value] = photosChantier.filter(p => p.category === cat.value);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-40">
        <RefreshCw className="w-6 h-6 text-[var(--oh-info-text)] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="photos-chantier">
      {/* Header avec actions */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-[var(--oh-text)] font-medium flex items-center gap-2">
            <Camera className="w-5 h-5 text-[var(--oh-info-text)]" />
            Photos Chantier
          </h4>
          <p className="text-[var(--oh-text-muted)] text-sm mt-1">
            {photosChantier.length} photo(s) • Documentez les étapes du chantier
          </p>
        </div>
        
        {!verrouille && (
          <div className="flex gap-2">
            {/* Sélecteur de catégorie */}
            <Select value={uploadCategory} onValueChange={setUploadCategory}>
              <SelectTrigger className="w-40 bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                {PHOTO_CATEGORIES.map(cat => (
                  <SelectItem key={cat.value} value={cat.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-2 h-2 rounded-full ${cat.color}`} />
                      {cat.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Bouton Import */}
            <Button
              variant="outline"
              onClick={() => setShowImportDialog(true)}
              className="border-[var(--oh-info-border)] text-[var(--oh-info-text)] hover:bg-[var(--oh-info-bg)]"
              disabled={photosExistantes.length === 0}
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Scanner dossier
            </Button>
            
            {/* Bouton Certificall - visible si fiche CEE applicable */}
            {certificallConfigured && (
              <Button
                variant="outline"
                onClick={() => {
                  loadCertificallCases();
                  setShowCertificallDialog(true);
                }}
                className="border-[var(--oh-stat-cyan-border)] text-[var(--oh-stat-cyan-text)] hover:bg-[var(--oh-stat-cyan-bg)]"
              >
                <Shield className="w-4 h-4 mr-2" />
                Certificall
              </Button>
            )}
            
            {/* Bouton Upload */}
            <Button
              className="bg-orange-600 hover:bg-orange-700"
              disabled={uploading}
            >
              <label className="flex items-center cursor-pointer">
                <Upload className="w-4 h-4 mr-2" />
                {uploading ? "Upload..." : "Ajouter"}
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleUpload}
                  className="hidden"
                />
              </label>
            </Button>
          </div>
        )}
      </div>

      {/* Tabs par catégorie */}
      <Tabs defaultValue="tous" className="w-full">
        <TabsList className="bg-[var(--oh-bg-elevated)] border border-[var(--oh-border)]">
          <TabsTrigger value="tous" className="data-[state=active]:bg-[var(--oh-bg-elevated)]">
            Tous ({photosChantier.length})
          </TabsTrigger>
          {PHOTO_CATEGORIES.map(cat => (
            <TabsTrigger 
              key={cat.value} 
              value={cat.value}
              className="data-[state=active]:bg-[var(--oh-bg-elevated)]"
            >
              <div className={`w-2 h-2 rounded-full ${cat.color} mr-2`} />
              {cat.label} ({photosByCategory[cat.value]?.length || 0})
            </TabsTrigger>
          ))}
        </TabsList>
        
        {/* Contenu Tous */}
        <TabsContent value="tous" className="mt-4">
          <PhotosGrid 
            photos={photosChantier}
            imageUrls={imageUrls}
            onPreview={setPreviewPhoto}
            onDownload={handleDownload}
            onDelete={handleDelete}
            verrouille={verrouille}
          />
        </TabsContent>
        
        {/* Contenu par catégorie */}
        {PHOTO_CATEGORIES.map(cat => (
          <TabsContent key={cat.value} value={cat.value} className="mt-4">
            <PhotosGrid 
              photos={photosByCategory[cat.value] || []}
              imageUrls={imageUrls}
              onPreview={setPreviewPhoto}
              onDownload={handleDownload}
              onDelete={handleDelete}
              verrouille={verrouille}
              emptyMessage={`Aucune photo "${cat.label}"`}
            />
          </TabsContent>
        ))}
      </Tabs>

      {/* Dialog Import depuis dossier */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[var(--oh-info-text)]" />
              Importer depuis le dossier
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[var(--oh-text-muted)] text-sm mb-4">
              Sélectionnez les photos à importer dans la documentation chantier.
              Ces photos proviennent de l'onglet "Photos" du dossier commercial.
            </p>
            
            {/* Sélecteur de catégorie pour import */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[var(--oh-text-muted)] text-sm">Catégorie :</span>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="w-48 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  {PHOTO_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${cat.color}`} />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Grille de sélection */}
            <div className="max-h-96 overflow-y-auto">
              {photosExistantes.length === 0 ? (
                <div className="text-center py-8 text-[var(--oh-text-muted)]">
                  <Image className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucune photo disponible dans le dossier</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                  {photosExistantes.map(photo => {
                    const isSelected = selectedForImport.has(photo.id);
                    const alreadyImported = photosChantier.some(p => p.source_id === photo.id);
                    
                    return (
                      <div 
                        key={photo.id}
                        onClick={() => !alreadyImported && toggleImportSelection(photo.id)}
                        className={`
                          relative rounded-lg overflow-hidden cursor-pointer
                          border-2 transition-all
                          ${alreadyImported 
                            ? 'border-[var(--oh-success-border)] opacity-50 cursor-not-allowed' 
                            : isSelected 
                              ? 'border-[var(--oh-info-border)] ring-2 ring-orange-500/30' 
                              : 'border-[var(--oh-border)] hover:border-[var(--oh-border)]'}
                        `}
                      >
                        {/* Image */}
                        <div className="aspect-square bg-[var(--oh-bg-elevated)]">
                          {imageUrls[photo.id] ? (
                            <img 
                              src={imageUrls[photo.id]} 
                              alt={photo.filename || photo.nom}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <FileImage className="w-8 h-8 text-[var(--oh-text-secondary)]" />
                            </div>
                          )}
                        </div>
                        
                        {/* Overlay sélection */}
                        {isSelected && !alreadyImported && (
                          <div className="absolute inset-0 bg-[var(--oh-info-bg)] flex items-center justify-center">
                            <CheckCircle2 className="w-8 h-8 text-[var(--oh-text)]" />
                          </div>
                        )}
                        
                        {/* Badge déjà importé */}
                        {alreadyImported && (
                          <div className="absolute inset-0 bg-[var(--oh-bg)] flex items-center justify-center">
                            <Badge className="bg-green-600">Déjà importé</Badge>
                          </div>
                        )}
                        
                        {/* Nom */}
                        <div className="absolute bottom-0 left-0 right-0 bg-[var(--oh-bg)] p-1">
                          <p className="text-xs text-[var(--oh-text)] truncate">
                            {photo.filename || photo.nom || photo.type}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <div className="flex items-center justify-between w-full">
              <span className="text-[var(--oh-text-muted)] text-sm">
                {selectedForImport.size} photo(s) sélectionnée(s)
              </span>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setShowImportDialog(false)}
                  className="border-[var(--oh-border)]"
                >
                  Annuler
                </Button>
                <Button 
                  onClick={handleImport}
                  disabled={selectedForImport.size === 0}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Importer ({selectedForImport.size})
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Preview */}
      <Dialog open={!!previewPhoto} onOpenChange={() => setPreviewPhoto(null)}>
        <DialogContent className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-w-4xl p-0">
          <div className="relative">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setPreviewPhoto(null)}
              className="absolute top-2 right-2 z-10 bg-[var(--oh-bg)] hover:bg-[var(--oh-bg)]"
            >
              <X className="w-4 h-4 text-[var(--oh-text)]" />
            </Button>
            
            {previewPhoto && imageUrls[previewPhoto.id || previewPhoto.source_id] && (
              <img 
                src={imageUrls[previewPhoto.id || previewPhoto.source_id]}
                alt={previewPhoto.filename}
                className="w-full max-h-[80vh] object-contain"
              />
            )}
            
            {previewPhoto && (
              <div className="absolute bottom-0 left-0 right-0 bg-[var(--oh-bg)] p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[var(--oh-text)] font-medium">{previewPhoto.filename}</p>
                    <div className="flex items-center gap-2 mt-1">
                      {PHOTO_CATEGORIES.find(c => c.value === previewPhoto.category) && (
                        <Badge className={PHOTO_CATEGORIES.find(c => c.value === previewPhoto.category).color}>
                          {PHOTO_CATEGORIES.find(c => c.value === previewPhoto.category).label}
                        </Badge>
                      )}
                      <span className="text-[var(--oh-text-muted)] text-sm">
                        {previewPhoto.date_ajout && formatDateFR(previewPhoto.date_ajout)}
                      </span>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => handleDownload(previewPhoto)}
                    className="border-[var(--oh-border)]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Import Certificall */}
      <Dialog open={showCertificallDialog} onOpenChange={setShowCertificallDialog}>
        <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Shield className="w-5 h-5 text-cyan-400" />
              Importer depuis Certificall
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-[var(--oh-text-muted)] text-sm mb-4">
              Récupérez les photos certifiées (horodatées, géolocalisées, signées eIDAS) depuis Certificall.
            </p>
            
            {/* Sélecteur de catégorie */}
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[var(--oh-text-muted)] text-sm">Catégorie cible :</span>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger className="w-48 bg-[var(--oh-bg-surface)] border-[var(--oh-border)]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
                  {PHOTO_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${cat.color}`} />
                        {cat.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Liste des dossiers Certificall */}
            <div className="max-h-80 overflow-y-auto space-y-2">
              {certificallLoading ? (
                <div className="text-center py-8 text-[var(--oh-text-muted)]">
                  <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
                  Recherche des dossiers...
                </div>
              ) : certificallCases.length === 0 ? (
                <div className="text-center py-8 text-[var(--oh-text-muted)]">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun dossier Certificall trouvé</p>
                  <p className="text-xs mt-2">
                    Référence recherchée : {dossierRef}
                  </p>
                </div>
              ) : (
                certificallCases.map(certCase => (
                  <div 
                    key={certCase.case_id}
                    className="flex items-center justify-between p-3 bg-[var(--oh-bg-elevated)] rounded-lg border border-[var(--oh-border)] hover:border-cyan-500/50"
                  >
                    <div>
                      <p className="text-[var(--oh-text)] font-medium">
                        {certCase.référence || certCase.case_id}
                      </p>
                      <div className="flex items-center gap-3 text-sm text-[var(--oh-text-muted)] mt-1">
                        <span>{certCase.items_count} photo(s)</span>
                        <Badge className={certCase.status === "closed" ? "bg-green-600" : "bg-orange-500"}>
                          {certCase.status === "closed" ? "Clôturé" : "En cours"}
                        </Badge>
                        {certCase.frame_name && (
                          <span className="text-cyan-400">{certCase.frame_name}</span>
                        )}
                      </div>
                    </div>
                    <Button
                      onClick={() => handleCertificallImport(certCase.case_id)}
                      className="bg-cyan-600 hover:bg-cyan-700"
                      disabled={certCase.items_count === 0}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Importer
                    </Button>
                  </div>
                ))
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCertificallDialog(false)}
              className="border-[var(--oh-border)]"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Composant grille de photos
function PhotosGrid({ photos, imageUrls, onPreview, onDownload, onDelete, verrouille, emptyMessage }) {
  if (photos.length === 0) {
    return (
      <div className="text-center py-12 text-[var(--oh-text-muted)]">
        <Camera className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>{emptyMessage || "Aucune photo"}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
      {photos.map(photo => {
        const category = PHOTO_CATEGORIES.find(c => c.value === photo.category);
        
        return (
          <div 
            key={photo.id}
            className="relative group rounded-lg overflow-hidden border border-[var(--oh-border)] hover:border-[var(--oh-border)] transition-all"
          >
            {/* Image */}
            <div className="aspect-square bg-[var(--oh-bg-elevated)]">
              {imageUrls[photo.id || photo.source_id] ? (
                <img 
                  src={imageUrls[photo.id || photo.source_id]}
                  alt={photo.filename}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <FileImage className="w-8 h-8 text-[var(--oh-text-secondary)]" />
                </div>
              )}
            </div>
            
            {/* Badge catégorie */}
            {category && (
              <div className="absolute top-2 left-2">
                <Badge className={`${category.color} text-xs`}>
                  {category.label}
                </Badge>
              </div>
            )}
            
            {/* Badge origine */}
            {photo.origine === "import_dossier" && (
              <div className="absolute top-2 right-2">
                <Badge className="bg-[var(--oh-accent-solid)] text-xs">Importé</Badge>
              </div>
            )}
            
            {/* Actions (hover) */}
            <div className="absolute inset-0 bg-[var(--oh-bg)] opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onPreview(photo)}
                className="bg-[var(--oh-bg-surface)]/20 hover:bg-[var(--oh-bg-surface)]/30"
              >
                <Eye className="w-4 h-4 text-[var(--oh-text)]" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onDownload(photo)}
                className="bg-[var(--oh-bg-surface)]/20 hover:bg-[var(--oh-bg-surface)]/30"
              >
                <Download className="w-4 h-4 text-[var(--oh-text)]" />
              </Button>
              {!verrouille && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => onDelete(photo.id)}
                  className="bg-[var(--oh-error-bg)] hover:bg-[var(--oh-error-bg)]"
                >
                  <Trash2 className="w-4 h-4 text-[var(--oh-text)]" />
                </Button>
              )}
            </div>
            
            {/* Nom fichier */}
            <div className="absolute bottom-0 left-0 right-0 bg-[var(--oh-bg)] p-2">
              <p className="text-xs text-[var(--oh-text)] truncate">{photo.filename}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
